import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { keyframes } from '@mui/system';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 4px 20px rgba(85,180,172,0.35); }
  50%       { box-shadow: 0 4px 40px rgba(85,180,172,0.65); }
`;

const TIERS = [
  {
    title: 'Starter',
    price: '$40',
    period: '/mes · por sucursal',
    description: 'Ideal para pequeñas empresas que están comenzando.',
    features: [
      'Hasta 4 usuarios',
      '40 préstamos activos',
      'Módulos core incluidos',
      'Soporte técnico',
    ],
    isPopular: false,
    cta: 'Contáctanos',
  },
  {
    title: 'Business',
    price: '$99',
    period: '/mes · por sucursal',
    description: 'Para organizaciones en crecimiento con múltiples sucursales.',
    features: [
      'Hasta 8 usuarios',
      'Hasta 600 préstamos activos',
      'Módulos core incluidos',
      'Soporte técnico prioritario',
    ],
    isPopular: true,
    cta: 'Empezar ahora',
  },
  {
    title: 'Enterprise',
    price: '$320',
    period: '/mes · por sucursal',
    description: 'Solución a medida para grandes organizaciones.',
    features: [
      'Hasta 15 usuarios',
      'Hasta 1,200 préstamos activos',
      'Módulos core incluidos',
      'Soporte técnico prioritario',
    ],
    isPopular: false,
    cta: 'Contáctanos',
  },
];

function SideCard({ tier, index }) {
  return (
    <Box
      sx={{
        flex: 1,
        borderRadius: 3,
        border: '1px solid rgba(85,180,172,0.2)',
        background: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        transition: 'all 0.35s ease',
        animation: `${fadeInUp} 0.75s ease-out ${index * 0.15}s both`,
        boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: '0 14px 40px rgba(85,180,172,0.18), 0 4px 12px rgba(0,0,0,0.06)',
          borderColor: 'rgba(85,180,172,0.45)',
        },
        /* top gradient bar */
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: '3px',
          background: 'linear-gradient(90deg, #55b4ac, #18586b)',
        },
      }}
    >
      <Box sx={{ p: 3.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: 'text.primary', mb: 2.5 }}>
          {tier.title}
        </Typography>

        <Box sx={{ mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'text.secondary', mb: 0.6 }}>
              USD
            </Typography>
            <Typography sx={{ fontSize: '3rem', fontWeight: 900, color: 'text.primary', lineHeight: 1, letterSpacing: '-0.04em' }}>
              {tier.price}
            </Typography>
          </Box>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.78rem', mt: 0.5 }}>
            {tier.period}
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.88rem', lineHeight: 1.6, mb: 3 }}>
          {tier.description}
        </Typography>

        <Divider sx={{ mb: 2.5, borderColor: 'rgba(85,180,172,0.15)' }} />

        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {tier.features.map((f) => (
            <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 20, height: 20, borderRadius: '50%',
                background: 'rgba(85,180,172,0.12)',
                flexShrink: 0,
              }}>
                <CheckRoundedIcon sx={{ fontSize: 13, color: '#18586b' }} />
              </Box>
              <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.875rem' }}>
                {f}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ p: 3.5, pt: 0 }}>
        <Button
          fullWidth
          variant="outlined"
          href="https://wa.me/18299665507?text=Hola!%20me%20interesa%20Bankivo"
          sx={{
            py: 1.4,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.95rem',
            borderColor: 'rgba(85,180,172,0.4)',
            color: '#18586b',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: '#55b4ac',
              background: 'rgba(85,180,172,0.06)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          {tier.cta}
        </Button>
      </Box>
    </Box>
  );
}

function PopularCard({ tier }) {
  return (
    <Box
      sx={{
        flex: 1,
        borderRadius: 3,
        background: 'linear-gradient(145deg, #08172a 0%, #0d2e42 45%, #18586b 100%)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        transform: { xs: 'none', md: 'scale(1.06)' },
        zIndex: 1,
        border: '1px solid rgba(85,180,172,0.35)',
        boxShadow: '0 20px 60px rgba(8,23,42,0.55), 0 0 0 1px rgba(85,180,172,0.15)',
        animation: `${fadeInUp} 0.75s ease-out 0.15s both`,
        transition: 'all 0.35s ease',
        '&:hover': {
          transform: { xs: 'translateY(-6px)', md: 'scale(1.06) translateY(-6px)' },
          boxShadow: '0 28px 80px rgba(8,23,42,0.65), 0 0 60px rgba(85,180,172,0.2)',
        },
      }}
    >
      {/* "Más Popular" banner */}
      <Box sx={{
        background: 'linear-gradient(90deg, rgba(85,180,172,0.25), rgba(85,180,172,0.15))',
        borderBottom: '1px solid rgba(85,180,172,0.3)',
        py: 0.85,
        textAlign: 'center',
      }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#7dd3cf' }}>
          ✦ Más Popular
        </Typography>
      </Box>

      <Box sx={{ p: 3.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#ffffff', mb: 2.5 }}>
          {tier.title}
        </Typography>

        <Box sx={{ mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'rgba(255,255,255,0.5)', mb: 0.6 }}>
              USD
            </Typography>
            <Typography sx={{ fontSize: '3rem', fontWeight: 900, color: '#ffffff', lineHeight: 1, letterSpacing: '-0.04em' }}>
              {tier.price}
            </Typography>
          </Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem', mt: 0.5 }}>
            {tier.period}
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.62)', fontSize: '0.88rem', lineHeight: 1.6, mb: 3 }}>
          {tier.description}
        </Typography>

        <Divider sx={{ mb: 2.5, borderColor: 'rgba(255,255,255,0.1)' }} />

        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {tier.features.map((f) => (
            <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 20, height: 20, borderRadius: '50%',
                background: 'rgba(85,180,172,0.25)',
                flexShrink: 0,
              }}>
                <CheckRoundedIcon sx={{ fontSize: 13, color: '#55b4ac' }} />
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.875rem' }}>
                {f}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ p: 3.5, pt: 0 }}>
        <Button
          fullWidth
          variant="contained"
          href="https://wa.me/18299665507?text=Hola!%20me%20interesa%20Bankivo"
          sx={{
            py: 1.4,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.95rem',
            background: 'linear-gradient(135deg, #55b4ac 0%, #3d9e96 100%)',
            color: '#ffffff',
            animation: `${glowPulse} 3s ease-in-out infinite`,
            transition: 'all 0.3s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #65c4bc 0%, #4daeaa 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 30px rgba(85,180,172,0.55)',
            },
          }}
        >
          {tier.cta}
        </Button>
      </Box>
    </Box>
  );
}

export default function Pricing() {
  return (
    <Box
      id="pricing"
      sx={{
        py: { xs: 10, sm: 14 },
        background: '#ffffff',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          bottom: 0, left: '50%',
          transform: 'translateX(-50%)',
          width: '80%', height: '50%',
          background: 'radial-gradient(ellipse, rgba(85,180,172,0.05) 0%, transparent 70%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 7, sm: 9 }, animation: `${fadeInUp} 0.8s ease-out` }}>
          <Chip
            label="Precios"
            size="small"
            sx={{
              mb: 2,
              background: 'rgba(85,180,172,0.12)',
              border: '1px solid rgba(85,180,172,0.35)',
              color: '#18586b',
              fontWeight: 600,
              fontSize: '0.78rem',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          />
          <Typography
            component="h2"
            variant="h3"
            sx={{
              fontWeight: 800,
              color: 'text.primary',
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.6rem', md: '3rem' },
              letterSpacing: '-0.025em',
            }}
          >
            Precios simples y transparentes
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', fontSize: { xs: '1rem', sm: '1.1rem' }, maxWidth: 480, mx: 'auto' }}
          >
            Elige el plan que se adapte a las necesidades de tu organización
          </Typography>
        </Box>

        {/* Cards */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 3, md: 2 },
            alignItems: { md: 'center' },
            px: { md: 2 },
          }}
        >
          {TIERS.map((tier) =>
            tier.isPopular
              ? <PopularCard key={tier.title} tier={tier} />
              : <SideCard key={tier.title} tier={tier} index={TIERS.indexOf(tier)} />
          )}
        </Box>

        {/* Footer note */}
        <Typography
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
            fontSize: '0.82rem',
            mt: { xs: 5, md: 7 },
            animation: `${fadeInUp} 0.8s ease-out 0.5s both`,
          }}
        >
          Todos los planes incluyen prueba gratuita · Sin tarjeta de crédito · Cancela cuando quieras
        </Typography>
      </Container>
    </Box>
  );
}
